import {
  ATS_CALL_FROM_LOAD_ERROR_MESSAGE,
  ATS_CALLS_NOT_FOUND_MESSAGE
} from '@/constants/atsCalls';
import { AuthService } from '@/services/AuthService';
import { IAtsCallFromResponse } from '@/types/IAtsCallFromResponse';
import { IAtsCallsResponse } from '@/types/IAtsCallsResponse';
import { IAtsRecordLinkParams } from '@/types/IAtsRecordLinkParams';
import { IGetAtsCallsParams } from '@/types/IGetAtsCallsParams';
import { IMakeAtsCallParams } from '@/types/IMakeAtsCallParams';
import { api } from '@/utils/api';
import { CustomError, IGenericResponseRoot } from '@/utils/getErrorData';

// Работа с API для работы с ATS (Applicant Tracking System)

// Получение истории звонков
export const getAccountAtsCalls = async (
  params: IGetAtsCallsParams
): Promise<IGenericResponseRoot<IAtsCallsResponse>> => {
  try {
    const headers: Record<string, string> = {};

    if (AuthService.authToken !== null) {
      headers.Authorization = `Bearer ${AuthService.authToken}`;
    } else {
      await AuthService.checkUser();
      throw new Error('Пользователь не авторизирован');
    }

    const response = await api.get<IGenericResponseRoot<IAtsCallsResponse>>(
      `${import.meta.env.VITE_API_URL}/api/admin/ats/calls`,
      {
        headers,
        params
      }
    );

    if (response.status === 500) {
      throw new CustomError(
        'При получении истории звонков произошла необработанная ошибка'
      );
    }

    if (response.data.response === null) {
      throw new CustomError(ATS_CALLS_NOT_FOUND_MESSAGE);
    }

    return response.data;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
    throw error;
  }
};

// Получение ссылки на запись звонка по его ID
export const getAtsRecordLink = async (
  data: IAtsRecordLinkParams
): Promise<IGenericResponseRoot<string>> => {
  try {
    const headers: Record<string, string> = {};
    const params = { uuid: data.accountUuid };

    if (AuthService.authToken !== null) {
      headers.Authorization = `Bearer ${AuthService.authToken}`;
    } else {
      await AuthService.checkUser();
      throw new Error('Пользователь не авторизирован');
    }

    const response = await api.get<IGenericResponseRoot<string>>(
      `${import.meta.env.VITE_API_URL}/api/admin/ats/call/${data.recordUuid}/record`,
      {
        headers,
        params
      }
    );

    if (response.status === 500) {
      throw new CustomError(
        'При получении ссылки на запись звонка произошла необработанная ошибка'
      );
    }

    if (response.data.response === null) {
      throw new CustomError('Ошибка. Ссылки на запись звонка не было найдено.');
    }

    return response.data;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
    throw error;
  }
};

// Получение списка линий, с которых менеджер может совершить исходящий звонок
export const getAtsCallFromList = async (): Promise<
  IGenericResponseRoot<IAtsCallFromResponse>
> => {
  try {
    const headers: Record<string, string> = {};

    if (AuthService.authToken !== null) {
      headers.Authorization = `Bearer ${AuthService.authToken}`;
    } else {
      await AuthService.checkUser();
      throw new Error('Пользователь не авторизирован');
    }

    const response = await api.get<IGenericResponseRoot<IAtsCallFromResponse>>(
      `${import.meta.env.VITE_API_URL}/api/admin/ats/call-from`,
      { headers }
    );

    if (response.status === 500) {
      throw new CustomError(ATS_CALL_FROM_LOAD_ERROR_MESSAGE);
    }

    if (response.data.response === null) {
      throw new CustomError(ATS_CALL_FROM_LOAD_ERROR_MESSAGE);
    }

    return response.data;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
    throw error;
  }
};

// Инициация звонка пользователю через АТС.
// Успехом считается только статус 200 — тело ответа не разбираем.
export const makeAtsCall = async (
  params: IMakeAtsCallParams
): Promise<void> => {
  try {
    const headers: Record<string, string> = {};

    if (AuthService.authToken !== null) {
      headers.Authorization = `Bearer ${AuthService.authToken}`;
    } else {
      await AuthService.checkUser();
      throw new Error('Пользователь не авторизирован');
    }

    const response = await api.post(
      `${import.meta.env.VITE_API_URL}/api/admin/ats/make-call`,
      undefined,
      { headers, params }
    );

    if (response.status !== 200) {
      throw new CustomError(
        'Не удалось инициировать звонок. Попробуйте ещё раз.',
        response.status
      );
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
    throw error;
  }
};
